import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/auth';
import { useCart } from '@/store/cart';
import { deliveryFee } from '@/lib/delivery';
import { useFavourites } from '@/store/favourites';
import { findDemoProduct, type DemoProduct } from '@/data/demoProducts';
import { placeholder } from '@/data/catalogImages';
import { useProductView } from '@/lib/tracking';
import { stripeConfigured } from '@/lib/stripe';
import { Seo } from '@/components/Seo';
import { GuestCheckoutModal, type GuestCheckoutPayload } from '@/components/order/GuestCheckoutModal';
import { LockerMobilePrompt } from '@/components/order/LockerMobilePrompt';
import { productColour, garmentType } from '@/lib/catalog';
import { angleLabels } from '@/lib/imageAngle';
import { CompleteTheLook } from '@/components/product/CompleteTheLook';
import { RecommendedForYou } from '@/components/product/RecommendedForYou';
import { TrustBadge, type TrustTier } from '@/components/product/TrustBadge';

interface ProductDetail {
  id: string;
  title: string;
  brand: string;
  code?: string;
  category?: string;
  subcategory?: string;
  description: string;
  priceGBP: number;
  originalPriceGBP?: number;
  size: string;
  weight?: string;
  condition: string;
  verified: boolean;
  images: string[];
  seller: { id: string; name: string; rating: number; sales: number; joinedYear?: number; trustScore?: number; trustTier?: TrustTier };
  acceptsSwap: boolean;
  acceptsOffers?: boolean;
  state?: string;
  isOwnerPreview?: boolean;
  quantity?: number;
}

/** Friendly colour swatch hexes for the spec sheet. */
const COLOUR_HEX: Record<string, string> = {
  white: '#F4F1EA', black: '#15151A', navy: '#1D2735', blue: '#3A5BA0', grey: '#8A8A8A',
  cream: '#EDE4D2', camel: '#B07D4F', red: '#9E2B2B', pink: '#D98BA6', green: '#3F5A4D',
  plum: '#5A3A52', gold: '#D98A2B', yellow: '#E3C35A', orange: '#C2614A',
};
const titleCase = (s: string) => s.replace(/(^|\s|-)([a-z])/g, (_, p, c) => p + c.toUpperCase());
const humanize = (s?: string) => (s ? titleCase(s.replace(/[-_]/g, ' ')) : undefined);

/* Adapt a DemoProduct to the ProductDetail shape so the rest of the component
   doesn't care whether the data came from the API or the local demo catalogue. */
function fromDemo(p: DemoProduct): ProductDetail {
  return {
    id: p.id, brand: p.brand!, title: p.title, description: p.description,
    category: p.category, subcategory: p.subcategory,
    priceGBP: p.priceGBP, originalPriceGBP: p.originalPriceGBP,
    size: p.size ?? '', weight: (p as any).weight ?? undefined, condition: p.condition, verified: !!p.verified,
    images: p.images, seller: p.seller,
    acceptsSwap: p.acceptsSwap, acceptsOffers: p.acceptsOffers,
  };
}


const COND_LABELS: Record<string, string> = {
  NEW_TAGS:  'Never worn - tags still on',
  LIKE_NEW:  'Worn once',
  EXCELLENT: 'Worn twice',
  GOOD:      'Like new - light wear',
  FAIR:      'Well loved - clear wear',
};
const condLabel = (c: string) => COND_LABELS[c] ?? c.replace(/_/g, ' ').toLowerCase();
const COND_DOTS: Record<string, number> = { NEW_TAGS: 5, LIKE_NEW: 5, EXCELLENT: 4, GOOD: 3, FAIR: 2 };
const condDots = (c: string) => COND_DOTS[c] ?? 3;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data: membership } = useQuery<{
    tier: string; shippingCreditGBP?: number; voucherRedeemable?: boolean;
    usage?: { ordersLeft: number; spendLeftGBP: number; feeFree: boolean };
  }>({
    queryKey: ['membership'],
    queryFn: async () => (await api.get('/membership')).data,
    enabled: !!user,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const isMember = membership?.tier === 'MEMBER';
  const memberUsage = membership?.usage;
  const [useCredit, setUseCredit] = useState(false);
  // Souqli Credit - a refund the buyer already has, spendable here just like on the cart.
  const [useSouqliCredit, setUseSouqliCredit] = useState(true);
  const { data: wallet } = useQuery<{ buyer?: { creditGBP: number } }>({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get('/me/wallet')).data,
    enabled: !!user,
    retry: false,
  });
  const souqliCreditAvailable = wallet?.buyer?.creditGBP ?? 0;
  const { add: addToCart, has: hasInCart } = useCart();
  const { toggle: toggleFav, has: hasFav } = useFavourites();
  const [activeImg, setActiveImg] = useState(0);
  const [imgFailed, setImgFailed] = useState<Record<number, boolean>>({});
  const [showOffer, setShowOffer] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [delivery, setDelivery] = useState<'locker' | 'standard' | 'express'>('locker');
  // Cinematic stage: sticky buy dock on scroll.
  const [showDock, setShowDock] = useState(false);
  const [lightbox, setLightbox] = useState(false);   // full-screen gallery
  const touchX = useRef(0);                          // swipe origin
  const stageRef = useRef<HTMLDivElement>(null);
  const imgCountRef = useRef(1);

  // ← / → cycle the gallery (declared before any early return to keep hook order stable).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const n = imgCountRef.current;
      if (n < 2) return;
      if (e.key === 'Escape')     setLightbox(false);
      if (e.key === 'ArrowLeft')  setActiveImg((i) => (i - 1 + n) % n);
      if (e.key === 'ArrowRight') setActiveImg((i) => (i + 1) % n);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Track this item server-side so we can alert the shopper on a price drop.
  useEffect(() => {
    if (!user || !id) return;
    api.post('/me/watch', { listingId: id }).catch(() => undefined);
  }, [user, id]);

  // Reveal the floating buy dock once the stage scrolls out of view.
  useEffect(() => {
    const onScroll = () => {
      const el = stageRef.current;
      if (!el) return;
      setShowDock(el.getBoundingClientRect().bottom < 90);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const { data, isLoading, isError } = useQuery<ProductDetail>({
    queryKey: ['product', id],
    queryFn: async () => {
      // Demo IDs (d1, d2, …) come straight from the local catalogue so
      // homepage / category clicks always land on a populated detail page.
      const demo = id ? findDemoProduct(id) : undefined;
      if (demo) return fromDemo(demo);
      try {
        return (await api.get(`/products/${id}`)).data;
      } catch (err: any) {
        // Fall through to a demo card if the backend doesn't know this ID yet
        if (err?.response?.status === 404) {
          const fallback = id ? findDemoProduct(id) : undefined;
          if (fallback) return fromDemo(fallback);
        }
        throw err;
      }
    },
    enabled: !!id,
    retry: false,
  });

  const [showGuest, setShowGuest] = useState(false);
  const [needMobile, setNeedMobile] = useState(false);
  const buy = useMutation({
    mutationFn: async (guestInfo?: GuestCheckoutPayload) => {
      const validConds = ['NEW_TAGS', 'LIKE_NEW', 'EXCELLENT', 'GOOD', 'FAIR'];
      const snapshot = data ? {
        title: data.title,
        brand: data.brand,
        priceGBP: data.priceGBP,
        imageUrl: data.images?.[0],
        category: data.category,
        condition: validConds.includes(data.condition) ? data.condition : undefined,
      } : undefined;
      return (await api.post(`/orders`, { productId: id, deliveryService: delivery, preferDemo: !stripeConfigured, useShippingCredit: useCredit, useSouqliCredit, snapshot, ...(guestInfo ?? {}) })).data;
    },
    onSuccess: (order) => {
      setShowGuest(false);
      // Order + PaymentIntent created; collect payment on the checkout page.
      navigate(`/checkout/${order.id}`, {
        state: {
          clientSecret: order.clientSecret,
          demo: order.demo,
          paymentRef: order.paymentRef,
          amountGBP: order.breakdown?.totalGBP ?? data?.priceGBP,
          title: data?.title,
          reference: order.reference,
          productId: id,
          breakdown: order.breakdown,
          guest: order.guest,
          guestToken: order.guestToken,
        },
      });
    },
    onError: (e: any) => {
      if (e?.response?.data?.code === 'LOCKER_NEEDS_MOBILE' && user) { setNeedMobile(true); return; }
      toast.error(e?.response?.data?.message ?? 'Could not start checkout. Please try again.');
    },
  });

  const sendOffer = useMutation({
    mutationFn: async () => (await api.post(`/offers`, { listingId: id, amountGBP: Number(offerAmount) })).data,
    onSuccess: () => { toast.success('Offer sent to seller.'); setShowOffer(false); setOfferAmount(''); },
    onError: () => toast.error('Could not send offer.'),
  });

  // Track view + dwell time for the recommendation engine (no-op until data loads).
  useProductView(data ? { id: data.id, title: data.title, brand: data.brand, priceGBP: data.priceGBP } : null);

  if (isError) {
    return (
      <div className="max-w-[640px] mx-auto px-6 py-24 text-center">
        <Seo title="Product not found" noindex />
        <div className="text-5xl mb-4" aria-hidden>🔍</div>
        <h1 className="display text-3xl">Product not found</h1>
        <p className="text-mute mt-3 leading-relaxed max-w-[46ch] mx-auto">
          This listing has sold, been removed, or the link is not quite right. It may already have found a new home.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/c/all" className="btn btn-primary">Browse all listings</Link>
          <Link to="/" className="btn btn-outline">Back to home</Link>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="max-w-[1560px] mx-auto px-6 py-12 grid md:grid-cols-2 gap-12">
        <div className="aspect-square rounded-[26px] skeleton" />
        <div className="space-y-4">
          <div className="h-3 w-24 skeleton rounded" />
          <div className="h-10 w-3/4 skeleton rounded" />
          <div className="h-12 w-32 skeleton rounded" />
        </div>
      </div>
    );
  }

  // Gallery shows ONLY the seller's uploaded photos - never a stock/garment image,
  // so a product never displays a picture its seller didn't upload.
  const garment = garmentType({ title: data.title, brand: data.brand, category: data.category });
  const realImgs = (data.images ?? []).filter(Boolean);
  const images = realImgs.length ? realImgs : [placeholder(`${data.brand ?? ''} ${data.title}`.trim(), data.category ?? '')];
  // Each thumbnail is tagged from the photo itself (stored angle → file-name
  // hints → garment-aware fallback), so "Sole" only ever shows on footwear.
  const ANGLE_LABELS = angleLabels(images, { garment, category: data.category });
  imgCountRef.current = images.length;
  const inCart = hasInCart(data.id);
  const minOffer = Math.floor(data.priceGBP * 0.7);
  const discount = data.originalPriceGBP && data.originalPriceGBP > data.priceGBP
    ? Math.round((1 - data.priceGBP / data.originalPriceGBP) * 100)
    : null;

  const colour = productColour({ title: data.title, brand: data.brand });

  // Data-driven specification: each row is included only when its value exists,
  // and a group is dropped entirely when it ends up empty - so the card adapts
  // automatically to however much detail a listing supplies.
  type SpecItem = { k: string; v: string; dots?: number; swatch?: string; good?: boolean };
  const specGroups = ([
    {
      title: 'Item',
      rows: [
        { k: 'Brand', v: data.brand },
        humanize(data.category) ? { k: 'Category', v: humanize(data.category)! } : null,
        humanize(data.subcategory) ? { k: 'Type', v: humanize(data.subcategory)! } : null,
        data.code ? { k: 'Item code', v: data.code } : null,
      ],
    },
    {
      title: 'Condition & make',
      rows: [
        { k: 'Condition', v: condLabel(data.condition), dots: condDots(data.condition) },
        colour ? { k: 'Colour', v: titleCase(colour), swatch: COLOUR_HEX[colour] ?? colour } : null,
        data.size ? { k: 'Size / spec', v: data.size } : null,
        data.weight ? { k: 'Weight', v: data.weight } : null,
      ],
    },
    (data.originalPriceGBP || discount) ? {
      title: 'Pricing',
      rows: [
        { k: 'Asking price', v: `£${data.priceGBP.toFixed(0)}` },
        data.originalPriceGBP ? { k: 'Original / RRP', v: `£${data.originalPriceGBP.toFixed(0)}` } : null,
        discount ? { k: 'You save', v: `${discount}% off RRP`, good: true } : null,
      ],
    } : null,
    {
      title: 'Buying options',
      rows: [
        { k: 'Offers', v: data.acceptsOffers ? 'Accepted' : 'Fixed price', good: !!data.acceptsOffers },
        { k: 'Swap', v: data.acceptsSwap ? 'Open to swaps' : 'Not available', good: !!data.acceptsSwap },
        { k: 'Delivery', v: 'Locker · Tracked · Express' },
        { k: 'Buyer protection', v: '48h dispute window' },
      ],
    },
    {
      title: 'Trust',
      rows: [
        { k: 'Authenticity', v: data.verified ? 'Verified by Souqli' : 'Pending review', good: data.verified },
        { k: 'Seller', v: `${data.seller.name} · ★ ${data.seller.rating.toFixed(1)}` },
        { k: 'Track record', v: `${data.seller.sales} completed sales` },
      ],
    },
  ] as ({ title: string; rows: (SpecItem | null)[] } | null)[])
    .flatMap((g) => {
      if (!g) return [];
      const rows = g.rows.filter((r): r is SpecItem => !!r);
      return rows.length ? [{ title: g.title, rows }] : [];
    });

  const currentSrc = imgFailed[activeImg]
    ? placeholder(`${data.brand ?? ''} ${data.title}`.trim(), data.category ?? '')
    : images[activeImg];

  // ── Amazon-style commerce signals (deterministic per item so they're stable) ──
  // Real seller ratings only - never fabricated.
  const rating = data.seller.rating ?? 0;
  const reviewCount = data.seller.sales ?? 0;
  const hasRatings = reviewCount > 0 && rating > 0;
  const ETA_DAYS: Record<string, [number, number]> = { locker: [2, 4], standard: [3, 5], express: [1, 2] };
  const [etaLo, etaHi] = ETA_DAYS[delivery] ?? [2, 4];
  const fmtDate = (days: number) => new Date(Date.now() + days * 86_400_000).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const arrival = `${fmtDate(etaLo)} - ${fmtDate(etaHi)}`;

  const isSaved = hasFav(data.id);
  // Is THIS order still covered by the member's monthly allowance?
  const memberFeeFree = isMember && (memberUsage
    ? memberUsage.ordersLeft > 0 && data.priceGBP <= memberUsage.spendLeftGBP
    : true);

  return (
    <div className="bg-cream">
      <Seo
        title={`${data.brand ? data.brand + ' ' : ''}${data.title}`.trim()}
        description={`${data.brand ? data.brand + ' ' : ''}${data.title}, £${data.priceGBP.toFixed(0)}. Verified and payment-protected on Souqli, ready to ship.`}
        image={images[0]}
      />

      {/* Owner preview banner: this listing is not live to buyers yet. */}
      {data.isOwnerPreview && (
        <div className="section-dark bg-ink text-cream text-center text-[13px] px-4 py-2.5">
          <span className="pill pill-gold text-[9px] mr-2">PREVIEW</span>
          {data.state === 'PENDING_VERIFICATION'
            ? 'This is how buyers will see your listing once it is approved. Only you can see it right now.'
            : data.state === 'SOLD'
              ? 'This item has sold. This is a preview of the listing.'
              : 'This listing is not live to buyers yet. Only you can see this preview.'}
          <Link to="/dashboard/listings" className="text-gold underline ml-2">Back to my listings</Link>
        </div>
      )}

      {/* ══════════ THE VITRINE · cinematic product stage ══════════ */}
      {/* Same espresso-and-amber palette as the Membership page for consistency. */}
      <section
        ref={stageRef}
        className="section-dark relative text-cream overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0F0F12 0%,#1a1208 50%,#0F0F12 100%)' }}
      >
        {/* Ambient light bleed - the product's own photo, blown up and blurred */}
        <img
          src={currentSrc} alt="" aria-hidden
          className="absolute inset-0 w-full h-full object-cover scale-150 blur-[70px] opacity-30 transition-all duration-1000"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,rgba(15,15,18,.88) 0%,rgba(26,18,8,.78) 50%,rgba(15,15,18,.92) 100%)' }} />
        {/* Warm amber pool, matching the membership hero glow */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(201,169,97,0.12) 0%, transparent 62%)' }} />
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-10 pointer-events-none" />
        <div className="absolute inset-0 mashrabiya opacity-[0.05] pointer-events-none" />

        <div className="relative max-w-[1560px] mx-auto px-5 md:px-8 pt-4 pb-7">
          {/* Breadcrumb */}
          <nav className="stamp text-cream/45 mb-5 flex items-center gap-2 text-[10px]">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span>/</span>
            <Link to="/c/all" className="hover:text-gold">Shop</Link>
            <span>/</span>
            <Link to={`/c/${data.brand?.toLowerCase().replace(/\s+/g,'-') ?? 'all'}`} className="hover:text-gold">{data.brand}</Link>
            <span>/</span>
            <span className="text-cream/80">{data.title}</span>
          </nav>

          {/* items-stretch keeps the buy panel exactly as tall as the gallery */}
          <div className="grid lg:grid-cols-[minmax(0,1fr)_400px] gap-6 lg:gap-8 items-stretch">

            {/* ── Media column: thumbnail rail on the left, stage on the right ── */}
            <div className="order-1 lg:sticky lg:top-24 mr-auto w-full lg:w-[90%] grid grid-cols-1 sm:grid-cols-[84px_minmax(0,1fr)] gap-2.5 sm:gap-3">

              {/* Front / back / side / detail / tag - stacked down the left,
                  sharing the stage height so the rail never overhangs it. */}
              <div className="hidden sm:flex flex-col gap-2 h-full">
                {images.slice(0, 6).map((src, i) => (
                  <button
                    key={i}
                    onMouseEnter={() => setActiveImg(i)}
                    onClick={() => setActiveImg(i)}
                    aria-label={`View ${ANGLE_LABELS[i] ?? `image ${i + 1}`}`}
                    className={`group relative flex-1 min-h-0 rounded-lg overflow-hidden transition-all duration-300 ${
                      activeImg === i
                        ? 'ring-2 ring-gold shadow-glow'
                        : 'ring-1 ring-cream/15 opacity-55 hover:opacity-100 hover:ring-gold/50'
                    }`}
                  >
                    <img
                      src={src}
                      alt={`${data.title} - ${ANGLE_LABELS[i] ?? `view ${i + 1}`}`}
                      onError={() => setImgFailed((m) => ({ ...m, [i]: true }))}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <span className={`absolute bottom-0 inset-x-0 text-center text-[6px] tracking-[0.12em] py-[2px] backdrop-blur-sm ${
                      activeImg === i ? 'bg-gold/90 text-ink font-bold' : 'bg-ink/60 text-cream/75'
                    }`}>
                      {(ANGLE_LABELS[i] ?? `0${i + 1}`).toUpperCase()}
                    </span>
                  </button>
                ))}
              </div>

              <div
                className="relative w-full aspect-[4/5] sm:aspect-[4/3.2] rounded-2xl overflow-hidden bg-ink-800/50 ring-1 ring-cream/12 shadow-frame group cursor-zoom-in"
                onClick={() => setLightbox(true)}
                onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                  const dx = e.changedTouches[0].clientX - touchX.current;
                  if (Math.abs(dx) > 45) {
                    setActiveImg((i) => (dx < 0 ? (i + 1) % images.length : (i - 1 + images.length) % images.length));
                  }
                }}
              >
                {/* The piece fills the frame edge to edge - full scale, no letterboxing */}
                <img
                  key={activeImg}
                  src={currentSrc}
                  alt={data.title}
                  onError={(e) => {
                    setImgFailed((m) => ({ ...m, [activeImg]: true }));
                    (e.target as HTMLImageElement).src = placeholder(`${data.brand} ${data.title}`);
                  }}
                  className="absolute inset-0 w-full h-full object-cover animate-fade-in transition-transform duration-700 ease-souq group-hover:scale-[1.06]"
                />
                {/* readability veils, top and bottom only - the piece stays crisp */}
                <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-ink/45 to-transparent pointer-events-none" />
                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-ink/55 to-transparent pointer-events-none" />

                {/* floating chips */}
                {data.verified && (
                  <Link to="/trust" className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full bg-cream/95 px-3 py-1.5 text-[11px] font-semibold text-gold-dark shadow-sm ring-1 ring-gold/30 hover:ring-gold/60 transition" title="What Verified by Souqli means">
                    ✓ Verified by Souqli
                  </Link>
                )}
                {discount && (
                  <span className="absolute bottom-5 left-5 rounded-full bg-black text-purewhite text-[12px] font-bold px-3.5 py-1.5 shadow-glow">−{discount}%</span>
                )}
                <span className="absolute top-5 right-5 flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFav({ id: data.id, title: data.title, brand: data.brand, priceGBP: data.priceGBP, imageUrl: images[0], condition: data.condition, originalPriceGBP: data.originalPriceGBP });
                      // Keep the server watchlist in step so price-drop alerts follow the heart.
                      if (user) {
                        if (isSaved) api.delete(`/me/watch/${data.id}`).catch(() => undefined);
                        else api.post('/me/watch', { listingId: data.id, saved: true }).catch(() => undefined);
                      }
                      toast.success(isSaved ? 'Removed from saved.' : 'Saved - we’ll alert you if the price drops.');
                    }}
                    aria-label={isSaved ? 'Remove from saved' : 'Save item'}
                    className={`w-10 h-10 rounded-full grid place-items-center backdrop-blur transition-all ${isSaved ? 'bg-gold text-ink' : 'bg-ink/55 text-cream hover:bg-ink/75'}`}
                  >
                    <svg width="19" height="19" viewBox="0 0 24 24" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 21s-7-4.5-9.3-9.2C1 8.4 3.4 5 6.7 5c2 0 3.4 1.1 4.3 2.5C12 6.1 13.4 5 15.4 5c3.3 0 5.7 3.4 4 6.8C19 16.5 12 21 12 21z" />
                    </svg>
                  </button>
                </span>

                {/* prev / next */}
                {images.length > 1 && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + images.length) % images.length); }} aria-label="Previous image"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-ink/55 backdrop-blur text-cream grid place-items-center opacity-0 group-hover:opacity-100 hover:bg-gold hover:text-ink transition">‹</button>
                    <button onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % images.length); }} aria-label="Next image"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-ink/55 backdrop-blur text-cream grid place-items-center opacity-0 group-hover:opacity-100 hover:bg-gold hover:text-ink transition">›</button>
                  </>
                )}

                {/* progress bars - clearer than a plain counter */}
                <div className="absolute bottom-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                  {images.map((_, i) => (
                    <span key={i} className={`h-[3px] rounded-full transition-all duration-300 ${activeImg === i ? 'w-6 bg-gold' : 'w-2 bg-cream/35'}`} />
                  ))}
                </div>

                <span className="absolute bottom-3.5 right-4 flex items-center gap-1.5 rounded-full bg-ink/60 backdrop-blur px-2.5 py-1 text-[9px] tracking-widest text-cream/75 opacity-0 group-hover:opacity-100 transition">
                  ⤢ TAP TO ENLARGE
                </span>
              </div>

            </div>

            {/* ── Glass buy panel ── */}
            <aside className="order-2 lg:sticky lg:top-24 lg:self-stretch flex">
              <div className="w-full flex flex-col rounded-[22px] bg-white/[0.07] backdrop-blur-xl ring-1 ring-cream/15 shadow-frame p-5">
                <Link to={`/c/${data.brand?.toLowerCase().replace(/\s+/g, '-') ?? 'all'}`} className="stamp text-gold text-[10px] hover:underline">{data.brand}</Link>
                <h1 className="display text-[25px] leading-[1.15] mt-1 text-cream">{data.title}</h1>

                {hasRatings && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1">
                    <Stars value={rating} size={13} />
                    <span className="text-[13px] font-medium text-cream">{rating.toFixed(1)}</span>
                    <a href="#reviews" className="text-[12px] text-gold hover:underline">{reviewCount.toLocaleString()} {reviewCount === 1 ? 'rating' : 'ratings'}</a>
                  </div>
                )}

                {/* price + chips share one band */}
                <div className="mt-3.5 pt-3.5 border-t border-cream/10 flex items-end justify-between gap-3 flex-wrap">
                  <div>
                    <div className="display text-[36px] text-gold leading-none">£{data.priceGBP.toFixed(0)}</div>
                    {data.originalPriceGBP && data.originalPriceGBP > data.priceGBP && (
                      <div className="text-[11px] text-cream/50 mt-1.5">
                        RRP <span className="line-through">£{data.originalPriceGBP.toFixed(0)}</span>
                        <span className="text-sage-light font-semibold ml-2">Save £{(data.originalPriceGBP - data.priceGBP).toFixed(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] text-sage-light font-medium flex items-center gap-1.5 pb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-sage-light" /> In stock · {typeof data.quantity === 'number' && data.quantity > 1 ? `${data.quantity} available` : '1 only'}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-gold text-ink text-[10px] font-semibold px-2.5 py-0.5">{condLabel(data.condition)}</span>
                  {data.size && <span className="rounded-full bg-cream/10 ring-1 ring-cream/20 text-cream text-[10px] px-2.5 py-0.5">Size {data.size}</span>}
                  {data.weight && <span className="rounded-full bg-cream/10 ring-1 ring-cream/20 text-cream text-[10px] px-2.5 py-0.5">Weight {data.weight}</span>}
                  {colour && (
                    <span className="rounded-full bg-cream/10 ring-1 ring-cream/20 text-cream text-[10px] px-2.5 py-0.5 inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full ring-1 ring-cream/30" style={{ background: COLOUR_HEX[colour] ?? colour }} />
                      {titleCase(colour)}
                    </span>
                  )}
                  {data.acceptsSwap && <span className="rounded-full bg-clay/30 ring-1 ring-clay/40 text-cream text-[10px] px-2.5 py-0.5">Swap-able</span>}
                </div>

                {/* delivery - compact 3-across picker */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="stamp text-gold text-[9px]">DELIVERY</span>
                    <span className="text-[11px] text-sage-light font-medium">Arrives {arrival}</span>
                  </div>
                  <div className="space-y-1.5">
                    {([
                      { v: 'locker',   icon: '📦', carrier: 'InPost',     label: 'Locker pickup',   eta: '2-4 days', fee: `£${deliveryFee('locker', (data as any).parcelTier).toFixed(2)}` },
                      { v: 'standard', icon: '📮', carrier: 'Royal Mail', label: 'Tracked to door', eta: '3-5 days', fee: `£${deliveryFee('standard', (data as any).parcelTier).toFixed(2)}` },
                      { v: 'express',  icon: '🚀', carrier: 'DPD',        label: 'Express courier', eta: '1-2 days', fee: `£${deliveryFee('express', (data as any).parcelTier).toFixed(2)}` },
                    ] as const).map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setDelivery(o.v)}
                        className={`w-full flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition ${
                          delivery === o.v ? 'border-gold bg-gold/15' : 'border-cream/15 hover:border-gold/50'
                        }`}
                      >
                        <span className={`w-3.5 h-3.5 rounded-full border-2 grid place-items-center shrink-0 ${delivery === o.v ? 'border-gold' : 'border-cream/30'}`}>
                          {delivery === o.v && <span className="w-1.5 h-1.5 rounded-full bg-gold" />}
                        </span>
                        <span className="text-sm shrink-0">{o.icon}</span>
                        <span className="flex-1 min-w-0">
                          <span className={`block text-[12.5px] font-bold leading-none ${delivery === o.v ? 'text-gold' : 'text-cream'}`}>
                            {o.carrier}
                          </span>
                          <span className="block text-[10.5px] text-cream/50 mt-0.5">{o.label} · {o.eta}</span>
                        </span>
                        <span className={`text-[12px] font-semibold shrink-0 ${delivery === o.v ? 'text-gold' : 'text-cream/80'}`}>{o.fee}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {delivery === 'locker' && (
                  <p className="mt-2 text-[10.5px] text-cream/50 leading-snug">📦 Locker collection, not home delivery. Your parcel goes to a nearby InPost locker and you open it with a one-time code we text you.</p>
                )}
                {isMember && membership?.voucherRedeemable === true && (
                  <label className="mt-2.5 flex items-start gap-2 text-[11px] text-cream/85 cursor-pointer rounded-xl border border-gold/40 bg-gold/12 px-2.5 py-1.5">
                    <input type="checkbox" className="mt-0.5 accent-gold" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} />
                    <span>Redeem my <span className="font-semibold text-gold">£4 delivery voucher</span>.</span>
                  </label>
                )}

                {souqliCreditAvailable > 0 && (
                  <label className="mt-2.5 flex items-start gap-2 text-[11px] text-cream/85 cursor-pointer rounded-xl border border-sage/40 bg-sage/12 px-2.5 py-1.5">
                    <input type="checkbox" className="mt-0.5 accent-gold" checked={useSouqliCredit} onChange={(e) => setUseSouqliCredit(e.target.checked)} />
                    <span>Use my <span className="font-semibold text-sage">£{souqliCreditAvailable.toFixed(2)} Souqli Credit</span> on this order.</span>
                  </label>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    disabled={buy.isPending || data.isOwnerPreview}
                    onClick={() => (user ? buy.mutate(undefined) : setShowGuest(true))}
                    className="btn btn-gold flex-[1.6] font-semibold"
                  >
                    {data.isOwnerPreview ? 'Your listing' : buy.isPending ? 'Placing…' : 'Buy now'}
                  </button>
                  <button
                    onClick={() => {
                      if (inCart) { navigate('/cart'); return; }
                      addToCart({ id: data.id, title: data.title, brand: data.brand, priceGBP: data.priceGBP, imageUrl: images[0], parcelTier: (data as any).parcelTier }, delivery);
                      toast.success('Added to your bag.');
                    }}
                    className="btn flex-1 border border-cream/30 text-cream hover:bg-cream hover:text-ink transition"
                  >
                    {inCart ? 'View bag' : '+ Bag'}
                  </button>
                </div>

                <p className="text-[10.5px] text-cream/50 text-center leading-snug mt-2.5">
                  {!isMember ? (
                    <>+ 4.5% + 70p buyer protection fee (free for Members within limits) at checkout.</>
                  ) : memberFeeFree ? (
                    <>
                      <span className="text-sage-light font-medium">Fee-free on this order ✓</span>
                      {memberUsage && <> &middot; {memberUsage.ordersLeft} orders / £{memberUsage.spendLeftGBP.toFixed(0)} left this month</>}
                      .
                    </>
                  ) : (
                    <>
                      <span className="text-gold font-medium">Monthly allowance used</span> &middot; this order is charged the
                      reduced <span className="text-gold">3.5% + 60p member rate</span> (guests pay 4.5% + 70p). Resets next month.
                    </>
                  )}
                </p>

                <div className="grid grid-cols-2 gap-2 mt-2.5">
                  <button onClick={() => (user ? setShowOffer(true) : navigate('/login'))} className="btn btn-sm border border-cream/25 text-cream hover:bg-cream/10 transition text-[12px]">Make an offer</button>
                  {data.acceptsSwap ? (
                    <button onClick={() => navigate(`/dashboard/swap?against=${data.id}`)} className="btn btn-sm border border-cream/25 text-cream hover:bg-cream/10 transition text-[12px]">Propose swap</button>
                  ) : (
                    <button className="btn btn-sm border border-cream/10 text-cream/35 cursor-not-allowed text-[12px]" disabled>No swaps</button>
                  )}
                </div>

                {/* seller - pinned to the foot of the panel so it lines up with
                    the bottom of the gallery */}
                <div className="mt-auto pt-3.5 border-t border-cream/10 flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full grid place-items-center display text-ink text-base shrink-0" style={{ background: 'linear-gradient(135deg,#E7A23E,#A66018)' }}>
                    {data.seller.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold leading-none text-cream truncate">{data.seller.name}</div>
                    <div className="text-[10.5px] text-cream/50 mt-1">{data.seller.sales} sales · ★ {data.seller.rating.toFixed(1)} · ID-verified</div>
                  </div>
                  <TrustBadge score={data.seller.trustScore} tier={data.seller.trustTier} showScore={false} />
                </div>
                <div className="mt-2.5 flex items-center gap-1.5 text-[10.5px] text-cream/50">
                  <span className="text-gold"><SpecIcon name="lock" small /></span> Payment protected until you confirm collection
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ══════════ Below the stage - glass detail panels ══════════ */}
      <div className="max-w-[1560px] mx-auto px-6 py-10 grid lg:grid-cols-12 gap-5">

        {/* ─── Specification ─── */}
        <Glass label="Specification" aside={<span className="mono text-[11px] text-mute">{data.code ?? 'SOUQLI · CERTIFIED'}</span>} className="lg:col-span-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-8">
            {specGroups.map((g) => (
              <div key={g.title} className="break-inside-avoid">
                <div className="text-[10px] tracking-widest uppercase text-gold-dark mb-1">{g.title}</div>
                <dl>
                  {g.rows.map((r) => (
                    <SpecRow key={r.k} k={r.k} v={r.v} dots={r.dots} swatch={r.swatch} good={r.good} />
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </Glass>

        {/* ═══ Ratings & reviews ═══ */}
        <Reviews id="reviews" rating={rating} count={reviewCount} sellerName={data.seller.name} />

        <div className="lg:col-span-12"><CompleteTheLook product={data} /></div>
      </div>

      <RecommendedForYou excludeId={data.id} />

      {/* ══════════ Floating buy dock - appears once the stage scrolls away ══════════ */}
      <div className={`fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:bottom-0 inset-x-0 z-40 transition-all duration-300 ${showDock ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`}>
        <div className="section-dark bg-ink/95 backdrop-blur-xl border-t border-gold/25 shadow-frame">
          <div className="max-w-[1560px] mx-auto px-5 md:px-8 py-3 flex items-center gap-4">
            <img src={currentSrc} alt="" aria-hidden className="w-12 h-12 rounded-xl object-cover ring-1 ring-cream/15 shrink-0 hidden sm:block" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-cream truncate leading-tight">{data.title}</div>
              <div className="text-[11px] text-cream/50 truncate">{data.brand} · {condLabel(data.condition)}</div>
            </div>
            <div className="display text-2xl text-gold leading-none shrink-0">£{data.priceGBP.toFixed(0)}</div>
            <button
              onClick={() => {
                if (inCart) { navigate('/cart'); return; }
                addToCart({ id: data.id, title: data.title, brand: data.brand, priceGBP: data.priceGBP, imageUrl: images[0], parcelTier: (data as any).parcelTier }, delivery);
                toast.success('Added to your bag.');
              }}
              className="btn btn-sm border border-cream/30 text-cream hover:bg-cream hover:text-ink transition shrink-0 hidden sm:inline-flex"
            >
              {inCart ? 'View bag' : '+ Bag'}
            </button>
            <button
              disabled={buy.isPending || data.isOwnerPreview}
              onClick={() => (user ? buy.mutate(undefined) : setShowGuest(true))}
              className="btn btn-gold btn-sm font-semibold shrink-0 px-6"
            >
              {buy.isPending ? 'Placing…' : 'Buy now'}
            </button>
          </div>
        </div>
      </div>

      <GuestCheckoutModal open={showGuest} onClose={() => setShowGuest(false)} submitting={buy.isPending} onConfirm={(p) => buy.mutate(p)} />
      <LockerMobilePrompt open={needMobile} onClose={() => setNeedMobile(false)} onSaved={() => { setNeedMobile(false); buy.mutate(undefined); }} />

      {/* ══════════ Full-screen gallery ══════════ */}
      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-md flex flex-col" onClick={() => setLightbox(false)}>
          <div className="flex items-center justify-between px-5 py-4 shrink-0">
            <div className="min-w-0">
              <div className="stamp text-gold text-[9px]">{data.brand?.toUpperCase()}</div>
              <div className="text-cream text-sm truncate max-w-[60vw]">{data.title}</div>
            </div>
            <button onClick={() => setLightbox(false)} aria-label="Close gallery"
              className="w-10 h-10 rounded-full bg-white/10 text-cream grid place-items-center hover:bg-white/20 transition shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="relative flex-1 min-h-0 flex items-center justify-center px-4" onClick={(e) => e.stopPropagation()}>
            <img key={`lb-${activeImg}`} src={currentSrc} alt={data.title}
                 className="max-h-full max-w-full object-contain animate-fade-in" />
            {images.length > 1 && (
              <>
                <button onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)} aria-label="Previous"
                  className="absolute left-4 w-12 h-12 rounded-full bg-white/10 text-cream grid place-items-center text-xl hover:bg-gold hover:text-ink transition">‹</button>
                <button onClick={() => setActiveImg((i) => (i + 1) % images.length)} aria-label="Next"
                  className="absolute right-4 w-12 h-12 rounded-full bg-white/10 text-cream grid place-items-center text-xl hover:bg-gold hover:text-ink transition">›</button>
              </>
            )}
          </div>

          <div className="shrink-0 px-5 py-4 flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
            {images.slice(0, 6).map((src, i) => (
              <button key={i} onClick={() => setActiveImg(i)}
                className={`w-16 h-16 rounded-lg overflow-hidden transition-all ${activeImg === i ? 'ring-2 ring-gold' : 'ring-1 ring-cream/20 opacity-50 hover:opacity-100'}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {showOffer && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setShowOffer(false)}>
          <div className="card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="stamp text-gold-dark">Make an offer</div>
            <div className="display text-3xl mt-2">{data.title}</div>
            <p className="text-sm text-mute mt-2">
              Listed at <span className="text-ink font-medium">£{data.priceGBP.toFixed(0)}</span>.
              Min offer £{minOffer}. Seller has 24h to accept, decline or counter.
            </p>
            <label className="label mt-5">Your offer (£)</label>
            <input
              type="number"
              className="input input-lg"
              min={minOffer}
              max={data.priceGBP}
              value={offerAmount}
              onChange={(e) => setOfferAmount(e.target.value)}
              placeholder={String(Math.floor(data.priceGBP * 0.85))}
            />
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowOffer(false)} className="btn btn-outline flex-1">Cancel</button>
              <button
                onClick={() => sendOffer.mutate()}
                disabled={!offerAmount || Number(offerAmount) < minOffer || sendOffer.isPending}
                className="btn btn-primary flex-1"
              >
                {sendOffer.isPending ? 'Sending…' : 'Send offer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Frosted-glass detail panel: translucent fill, hairline transparent border,
 * a light sheen across the top edge and a soft "reflector" shadow beneath.
 */
function Glass({ label, aside, className = '', children }: {
  label: string; aside?: React.ReactNode; className?: string; children: React.ReactNode;
}) {
  return (
    <section className={`relative ${className}`}>
      {/* reflector - soft shadow pooling under the panel */}
      <span aria-hidden className="absolute -bottom-3 inset-x-8 h-8 rounded-[50%] bg-ink/15 blur-2xl pointer-events-none" />

      <div className="relative rounded-3xl border border-white/60 bg-white/45 backdrop-blur-md shadow-[0_20px_45px_-24px_rgba(34,26,18,0.45)] overflow-hidden">
        {/* glass sheen */}
        <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/90 to-transparent" />
        <span aria-hidden className="absolute -top-16 -left-10 w-72 h-40 bg-white/40 blur-3xl rotate-[-12deg] pointer-events-none" />

        <div className="relative p-6 md:p-8">
          <div className="flex items-center gap-3 mb-5">
            <h2 className="stamp text-gold-dark text-[10px] tracking-widest uppercase whitespace-nowrap">{label}</h2>
            <span className="h-px flex-1 bg-gradient-to-r from-gold/40 to-transparent" />
            {aside}
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

function SpecRow({ k, v, dots, swatch, good }: { k: string; v: string; dots?: number; swatch?: string; good?: boolean }) {
  return (
    <div className="flex items-baseline gap-1.5 py-[7px]">
      <dt className="stamp text-mute text-[10px] shrink-0">{k}</dt>
      <span className="flex-1 border-b border-dotted border-warmline mb-1" />
      <dd className="text-[13px] text-ink font-semibold text-right flex items-center gap-2 min-w-0">
        {swatch && <span className="w-3.5 h-3.5 rounded-full border border-warmline shrink-0" style={{ background: swatch }} />}
        {dots !== undefined && (
          <span className="flex gap-0.5 shrink-0">
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i < dots ? 'bg-gold shadow-[0_0_4px_rgba(217,138,43,0.5)]' : 'bg-warmline'}`} />
            ))}
          </span>
        )}
        <span className={`truncate ${good ? 'text-sage-dark' : ''}`}>{v}</span>
      </dd>
    </div>
  );
}

/** Minimal line icons for spec groups + glance tiles (keyed by group title or name). */
function SpecIcon({ name, small }: { name: string; small?: boolean }) {
  const s = small ? 12 : 15;
  const t = name.toLowerCase();
  const path =
    t.includes('condition') || t.includes('make')
      ? <path d="M12 2l2.4 6.9L21.5 9l-5.8 4.1L18 20l-6-4.2L6 20l2.3-6.9L2.5 9l7.1-.1z" />
    : t.includes('pric')
      ? <><circle cx="12" cy="12" r="9" /><path d="M14.5 9c-.5-.9-1.5-1.4-2.5-1.4-1.6 0-2.6.9-2.6 2.2 0 3 5.4 1.4 5.4 4.4 0 1.4-1.1 2.2-2.8 2.2-1.1 0-2.1-.5-2.6-1.4" /></>
    : t.includes('buy') || t.includes('option')
      ? <><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></>
    : t.includes('trust') || t === 'shield'
      ? <><path d="M12 2l7 4v6c0 4.4-3 8.3-7 10-4-1.7-7-5.6-7-10V6z" /><path d="M9 12l2 2 4-4" /></>
    : t === 'ruler'
      ? <><path d="M3 8l5-5 13 13-5 5z" /><path d="M8 7l2 2M11 4l2 2M14 7l2 2M6 11l2 2" /></>
    : t === 'lock'
      ? <><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>
    : t === 'refresh'
      ? <><path d="M21 12a9 9 0 1 1-2.6-6.3M21 3v5h-5" /></>
    : <><path d="M20.6 13.4 12 22l-8-8 8.6-8.6A2 2 0 0 1 14 5h4a2 2 0 0 1 2 2v4a2 2 0 0 1-.4 1.4Z" /><circle cx="16" cy="9" r="1" /></>;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {path}
    </svg>
  );
}

/** Gold star rating with fractional fill (clipped overlay). */
function Stars({ value, size = 15 }: { value: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, (value / 5) * 100));
  return (
    <span className="relative inline-block leading-none select-none" style={{ fontSize: size }} aria-label={`${value.toFixed(1)} out of 5 stars`}>
      <span className="text-warmline">★★★★★</span>
      <span className="absolute inset-0 overflow-hidden text-gold" style={{ width: `${pct}%` }}>★★★★★</span>
    </span>
  );
}

/** Real seller ratings. Honest empty state until a buyer leaves one. */
function Reviews({ id, rating, count, sellerName }: {
  id: string; rating: number; count: number; sellerName: string;
}) {
  const has = count > 0 && rating > 0;
  if (!has) return null;   // nothing to show until a buyer has actually rated
  return (
    <div id={id} className="scroll-mt-24 lg:col-span-12">
    <Glass label="Ratings &amp; reviews">
      <div className="flex items-center gap-6">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="display text-5xl text-ink leading-none">{rating.toFixed(1)}</span>
              <span className="text-mute text-sm">/ 5</span>
            </div>
            <div className="mt-2"><Stars value={rating} size={18} /></div>
            <div className="text-[12px] text-mute mt-1.5">{count.toLocaleString()} verified {count === 1 ? 'rating' : 'ratings'} for {sellerName}</div>
          </div>
      </div>
    </Glass>
    </div>
  );
}
